import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "../api";
import { useAuth } from "../auth/AuthContext";
import { setLanguage, type Language } from "../i18n";
import { useDocumentTitle } from "../lib/title";

export function Login() {
  const { t, i18n } = useTranslation();
  useDocumentTitle(t("login.title"));
  const { login } = useAuth();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (password === "") return;
    setBusy(true);
    setError(null);
    try {
      await login(password);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.code : "generic");
      setBusy(false);
    }
  }

  return (
    <main className="screen screen-login">
      <header className="stack">
        <h1 className="title">{t("login.title")}</h1>
        <p className="subtitle">{t("login.subtitle")}</p>
      </header>
      <form className="stack" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">{t("login.password")}</span>
          <input className="input" type="password" autoComplete="current-password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        {error && (
          <p className="error" role="alert">
            {t(`login.${error}`, { defaultValue: t(`errors.${error}`, { defaultValue: t("errors.generic") }) })}
          </p>
        )}
        <button className="pill pill-ink pill-wide pill-large" type="submit" disabled={busy || password === ""}>
          {busy ? t("login.submitting") : t("login.submit")}
        </button>
      </form>
      <div className="segmented" role="group" aria-label={t("language.label")}>
        {(["en", "he"] as Language[]).map((language) => (
          <button key={language} type="button" className="segmented-option" aria-pressed={i18n.language === language} onClick={() => void setLanguage(language)}>
            {t(`language.${language}`)}
          </button>
        ))}
      </div>
    </main>
  );
}
